// src/pages/components/OwnerQuestionnaireForm.jsx
import React, { useEffect, useRef, useState } from "react";
import "./css/questionnareform.css";
import { request } from "../../services/server";
import OwnerImageUploader, { OWNER_IMAGE_ROLES } from "./OwnerImageUploader";

/**
 * OwnerQuestionnaireForm (workspace/orgnr scoped)
 *
 * Connects to your "bundle" endpoint so you can load/save
 * owner + ddq + risk via one backend surface.
 *
 * Uses:
 *  GET  /valuation/workspace/<orgnr>/quizzes/
 *  POST /valuation/workspace/<orgnr>/quizzes/  body: { owner: {...} }
 *
 * NOTE:
 * Customer concentration has been removed from this form to avoid overlap with RiskProfile.
 * (Removed fields: largest_customer_share_percent, top10_share_percent, customer_dependency_risks)
 */

function defaultQuizzesUrl(orgnr) {
  return `/valuation/workspace/${encodeURIComponent(orgnr)}/quizzes/`;
}

function OwnerQuestionnaireForm({ orgnr, onSaved, quizzesUrl }) {
  const effectiveQuizzesUrl = quizzesUrl || defaultQuizzesUrl(orgnr);
  const [formData, setFormData] = useState(() => emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const abortRef = useRef(null);

  // --- helpers for mapping API <-> form -----------------

  const fromApiToForm = (data) => {
    if (!data) return;

    setFormData({
      transaction: {
        dealType: data.deal_type || "",
        sharePercentage: data.share_percentage ?? "",
        reasonForSale: data.reason_for_sale || "",
        sellerRoleAfter: data.seller_role_after || "",
        includedAssets: data.included_assets || "",
        excludedItems: data.excluded_items || "",
        desiredClosingDate: data.desired_closing_date || "",
        dealStructure: data.deal_structure || "",
        otherNotes: data.other_notes || "",
        competitiveAdvantages: data.competitive_advantages || "",
        growthOpportunities: data.growth_opportunities || "",
      },
      ownership: {
        numberOfShareholders: data.number_of_shareholders ?? "",
        ownerDescription: data.owner_description || "",
        ownerInvolvement: data.owner_involvement || "",
        groupStructure: data.group_structure || "",
        successionPlan: data.succession_plan || "",
        shareholders: Array.isArray(data.shareholders)
          ? data.shareholders.map((s) => ({
              name: s?.name || "",
              sharePercent: s?.share_percent ?? "",
              role: s?.role || "",
            }))
          : [],
        // Structured Historik timeline — {year, event} rows. Composer
        // prefers this over regex parsing of ``group_structure``. Same
        // shareholders / ebitda_adjustments pattern: empty rows are
        // filtered on submit.
        historyMilestones: Array.isArray(data.history_milestones)
          ? data.history_milestones.map((m) => ({
              year: m?.year ?? "",
              event: m?.event || "",
            }))
          : [],
      },
      customers: {
        numberOfCustomers: data.number_of_customers ?? "",
        mainSegments: data.main_segments || "",
        referenceCustomers: data.reference_customers || "",
        recurringRevenuePercent: data.recurring_revenue_percent ?? "",
        projectRevenuePercent: data.project_revenue_percent ?? "",
        averageContractLengthMonths: data.average_contract_length_months ?? "",
        hasKeyAccountContracts: data.has_key_account_contracts || "",
      },
      market: {
        topCompetitors: data.top_competitors || "",
        marketSizeAndTrend: data.market_size_and_trend || "",
        geographies: data.geographies || "",
      },
      organisation: {
        headcountFTE: data.headcount_fte ?? "",
        headcountDescription: data.headcount_description || "",
        managementSummary: data.management_summary || "",
        keyPersonDependencies: data.key_person_dependencies || "",
        recruitmentChallenges: data.recruitment_challenges || "",
      },
      operations: {
        locationsDescription: data.locations_description || "",
        ownsPremises: data.owns_premises || "",
        equipmentAndMachinery: data.equipment_and_machinery || "",
        inventoryAndLogistics: data.inventory_and_logistics || "",
        keySuppliers: data.key_suppliers || "",
        supplierDependency: data.supplier_dependency || "",
        certifications: data.certifications || "",
      },
      legal: {
        keyCustomerContractsDesc: data.key_customer_contracts_desc || "",
        keySupplierContractsDesc: data.key_supplier_contracts_desc || "",
        leaseAgreementsDesc: data.lease_agreements_desc || "",
        regulatedBusiness: data.regulated_business || "",
        regulatoryRequirements: data.regulatory_requirements || "",
        disputes: data.disputes || "",
        disputesDescription: data.disputes_description || "",
        complianceIssues: data.compliance_issues || "",
        otherLegalRisks: data.other_legal_risks || "",
      },
      financial: {
        ownerSalaryAdjustment: data.owner_salary_adjustment ?? "",
        otherOwnerRelatedAdjustments: data.other_owner_related_adjustments || "",
        nonRecurringItemsDesc: data.non_recurring_items_desc || "",
        // Structured EBITDA-bridge adjustments. Shareholders-pattern
        // list — each row is { label, amountKsek, direction, category, notes }.
        // Empty rows are filtered on submit. Backend accepts as
        // ``ebitda_adjustments`` JSONField.
        ebitdaAdjustments: Array.isArray(data.ebitda_adjustments)
          ? data.ebitda_adjustments.map((r) => ({
              label: r.label || "",
              amountKsek: r.amount_ksek ?? "",
              direction: r.direction || "add_back",
              category: r.category || "",
              notes: r.notes || "",
            }))
          : [],
        forecastAvailable: data.forecast_available || "",
        forecastSummary: data.forecast_summary || "",
        // Structured 3-year forecast projections. Same shareholders-
        // pattern list. Each row: { year, revenueMsek, ebitdaMsek,
        // growthPct, marginPct, notes }. Empty rows filtered on submit.
        forecastProjections: Array.isArray(data.forecast_projections)
          ? data.forecast_projections.map((r) => ({
              year: r.year ?? "",
              revenueMsek: r.revenue_msek ?? "",
              ebitdaMsek: r.ebitda_msek ?? "",
              growthPct: r.growth_pct ?? "",
              marginPct: r.margin_pct ?? "",
              notes: r.notes || "",
            }))
          : [],
        // Structured growth initiatives — quantified seller-plans for
        // Value Creation section. Same add/remove pattern. Each row:
        // { label, market, timing, investmentMsek, breakevenMonths,
        //   initialCommitmentMsek, annualRevenueTargetMsek,
        //   annualEbitdaTargetMsek, notes }.
        growthInitiatives: Array.isArray(data.growth_initiatives)
          ? data.growth_initiatives.map((r) => ({
              label: r.label || "",
              market: r.market || "",
              timing: r.timing || "",
              investmentMsek: r.investment_msek ?? "",
              breakevenMonths: r.breakeven_months ?? "",
              initialCommitmentMsek: r.initial_commitment_msek ?? "",
              annualRevenueTargetMsek: r.annual_revenue_target_msek ?? "",
              annualEbitdaTargetMsek: r.annual_ebitda_target_msek ?? "",
              notes: r.notes || "",
            }))
          : [],
      },
      imageRoleIntents:
        data.image_role_intents && typeof data.image_role_intents === "object"
          ? data.image_role_intents
          : {},
    });

    setSavedAt(data.updated_at || null);
  };

  const toApiPayload = (form) => {
    const num = (v) => (v === "" || v === null || v === undefined ? null : v);

    return {
      // 1. Transaktion
      deal_type: form.transaction.dealType || "",
      share_percentage: num(form.transaction.sharePercentage),
      reason_for_sale: form.transaction.reasonForSale || "",
      seller_role_after: form.transaction.sellerRoleAfter || "",
      included_assets: form.transaction.includedAssets || "",
      excluded_items: form.transaction.excludedItems || "",
      desired_closing_date: form.transaction.desiredClosingDate || "",
      deal_structure: form.transaction.dealStructure || "",
      other_notes: form.transaction.otherNotes || "",
      competitive_advantages: form.transaction.competitiveAdvantages || "",
      growth_opportunities: form.transaction.growthOpportunities || "",

      // 2. Ägare & historik
      number_of_shareholders: num(form.ownership.numberOfShareholders),
      owner_description: form.ownership.ownerDescription || "",
      owner_involvement: form.ownership.ownerInvolvement || "",
      group_structure: form.ownership.groupStructure || "",
      succession_plan: form.ownership.successionPlan || "",
      shareholders: (form.ownership.shareholders || [])
        .map((s) => ({
          name: (s.name || "").trim(),
          share_percent:
            s.sharePercent === "" || s.sharePercent === null || s.sharePercent === undefined
              ? null
              : Number(s.sharePercent),
          role: (s.role || "").trim(),
        }))
        .filter((s) => s.name || s.share_percent !== null || s.role),
      // Structured Historik milestones. Filter empty stub rows (no
      // year AND no event) before submit so the seller can leave
      // draft slots between saves. Year is normalized to a string on
      // the wire so composer regex validation stays uniform whether
      // the browser stringified the input as "1996" or 1996.
      history_milestones: (form.ownership.historyMilestones || [])
        .filter((m) => (m.year || "").toString().trim() || (m.event || "").trim())
        .map((m) => ({
          year: (m.year || "").toString().trim(),
          event: (m.event || "").trim(),
        })),

      // 3. Kunder & intäkter (NO customer concentration here)
      number_of_customers: num(form.customers.numberOfCustomers),
      main_segments: form.customers.mainSegments || "",
      reference_customers: form.customers.referenceCustomers || "",
      recurring_revenue_percent: num(form.customers.recurringRevenuePercent),
      project_revenue_percent: num(form.customers.projectRevenuePercent),
      average_contract_length_months: num(form.customers.averageContractLengthMonths),
      has_key_account_contracts: form.customers.hasKeyAccountContracts || "",

      // 3b. Marknad & konkurrenter
      top_competitors: form.market.topCompetitors || "",
      market_size_and_trend: form.market.marketSizeAndTrend || "",
      geographies: form.market.geographies || "",

      // 4. Organisation & personal
      headcount_fte: num(form.organisation.headcountFTE),
      headcount_description: form.organisation.headcountDescription || "",
      management_summary: form.organisation.managementSummary || "",
      key_person_dependencies: form.organisation.keyPersonDependencies || "",
      recruitment_challenges: form.organisation.recruitmentChallenges || "",

      // 5. Drift, lokaler & leverantörer
      locations_description: form.operations.locationsDescription || "",
      owns_premises: form.operations.ownsPremises || "",
      equipment_and_machinery: form.operations.equipmentAndMachinery || "",
      inventory_and_logistics: form.operations.inventoryAndLogistics || "",
      key_suppliers: form.operations.keySuppliers || "",
      supplier_dependency: form.operations.supplierDependency || "",
      certifications: form.operations.certifications || "",

      // 6. Juridik & avtal
      key_customer_contracts_desc: form.legal.keyCustomerContractsDesc || "",
      key_supplier_contracts_desc: form.legal.keySupplierContractsDesc || "",
      lease_agreements_desc: form.legal.leaseAgreementsDesc || "",
      regulated_business: form.legal.regulatedBusiness || "",
      regulatory_requirements: form.legal.regulatoryRequirements || "",
      disputes: form.legal.disputes || "",
      disputes_description: form.legal.disputesDescription || "",
      compliance_issues: form.legal.complianceIssues || "",
      other_legal_risks: form.legal.otherLegalRisks || "",

      // 7. Finansiella justeringar & prognos
      owner_salary_adjustment: num(form.financial.ownerSalaryAdjustment),
      other_owner_related_adjustments: form.financial.otherOwnerRelatedAdjustments || "",
      non_recurring_items_desc: form.financial.nonRecurringItemsDesc || "",
      // Structured EBITDA-bridge. Filter out stub rows (no label AND
      // no amount) before submit so the seller can leave draft slots
      // between saves. Amount is kSEK on the wire.
      ebitda_adjustments: (form.financial.ebitdaAdjustments || [])
        .filter((r) => (r.label || "").trim() || r.amountKsek !== "")
        .map((r) => ({
          label: (r.label || "").trim(),
          amount_ksek: r.amountKsek === "" ? null : Number(r.amountKsek),
          direction: r.direction || "add_back",
          category: r.category || "",
          notes: (r.notes || "").trim(),
        })),
      forecast_available: form.financial.forecastAvailable || "",
      forecast_summary: form.financial.forecastSummary || "",
      // Structured 3-year projections. Filter empty stubs (no year AND
      // no revenue AND no EBITDA) before submit. Numbers coerced from
      // strings; empty string → null so the seeder's is_populated
      // check treats missing rows as unseeded.
      forecast_projections: (form.financial.forecastProjections || [])
        .filter((r) =>
          r.year !== "" || r.revenueMsek !== "" || r.ebitdaMsek !== ""
        )
        .map((r) => ({
          year: r.year === "" ? null : Number(r.year),
          revenue_msek: r.revenueMsek === "" ? null : Number(r.revenueMsek),
          ebitda_msek: r.ebitdaMsek === "" ? null : Number(r.ebitdaMsek),
          growth_pct: r.growthPct === "" ? null : Number(r.growthPct),
          margin_pct: r.marginPct === "" ? null : Number(r.marginPct),
          notes: (r.notes || "").trim(),
        })),
      // Structured growth initiatives — quantified seller-plans for
      // Value Creation section. Filter stubs (no label AND no market)
      // before submit. Numbers coerced from strings.
      growth_initiatives: (form.financial.growthInitiatives || [])
        .filter((r) => (r.label || "").trim() || (r.market || "").trim())
        .map((r) => ({
          label: (r.label || "").trim(),
          market: (r.market || "").trim(),
          timing: (r.timing || "").trim(),
          investment_msek: r.investmentMsek === "" ? null : Number(r.investmentMsek),
          breakeven_months: r.breakevenMonths === "" ? null : Number(r.breakevenMonths),
          initial_commitment_msek:
            r.initialCommitmentMsek === ""
              ? null
              : Number(r.initialCommitmentMsek),
          annual_revenue_target_msek:
            r.annualRevenueTargetMsek === ""
              ? null
              : Number(r.annualRevenueTargetMsek),
          annual_ebitda_target_msek:
            r.annualEbitdaTargetMsek === ""
              ? null
              : Number(r.annualEbitdaTargetMsek),
          notes: (r.notes || "").trim(),
        })),
      image_role_intents:
        form.imageRoleIntents && typeof form.imageRoleIntents === "object"
          ? form.imageRoleIntents
          : {},
    };
  };

  // --- load from API when orgnr changes ----------------

  useEffect(() => {
    if (!orgnr) {
      setLoading(false);
      return;
    }

    // cancel previous
    try {
      abortRef.current?.abort();
    } catch {}
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    let aborted = false;
    setLoading(true);
    setError("");

    request(effectiveQuizzesUrl, { signal: ctrl.signal })
      .then((bundle) => {
        if (aborted) return;
        if (bundle?.owner) fromApiToForm(bundle.owner);
        else setFormData(emptyForm());
      })
      .catch((err) => {
        if (aborted) return;
        console.error("Failed to load owner questionnaire", err);
        setError(
          err?.body?.detail ||
            "Kunde inte hämta ägarfrågeformuläret just nu. Du kan ändå fylla i svaren nedan."
        );
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });

    return () => {
      aborted = true;
      try {
        ctrl.abort();
      } catch {}
    };
  }, [orgnr]);

  const handleChange = (section, field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgnr) return;

    setSaving(true);
    setError("");

    try {
      const payload = toApiPayload(formData);

      // Save only the owner part through the unified endpoint
      const bundle = await request(effectiveQuizzesUrl, {
        method: "POST",
        body: { owner: payload },
      });

      const owner = bundle?.owner || null;
      const updated = owner?.updated_at || new Date().toISOString();

      setSavedAt(updated);
      if (onSaved && owner) onSaved(owner);
    } catch (err) {
      console.error("Failed to save owner questionnaire", err);
      setError(err?.body?.detail || "Kunde inte spara ägaruppgifterna. Försök igen senare.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Laddar ägarfrågeformulär…</p>;

  return (
    <form onSubmit={handleSubmit} className="riskprofile-form">
      {error && <p className="questionnaire-error">{error}</p>}

      {savedAt && (
        <p className="questionnaire-meta">
          Senast uppdaterad: {new Date(savedAt).toLocaleString("sv-SE")}
        </p>
      )}

      <div className="questionnaire-intro">
        <p>
          Ägarintervju med fokus på bakgrund till affären, ägarbild, organisation,
          kunder och finansiella justeringar. Svara hellre ungefärligt än perfekt –
          detaljer kan alltid kompletteras i nästa steg.
        </p>
        <p className="risk-help" style={{ marginTop: 8 }}>
          Obs: Kundkoncentration hanteras i riskprofilen (separat) och ingår därför inte här.
        </p>
      </div>

      {/* 1. Transaktion & försäljningsskäl */}
      <fieldset className="risk-card">
        <legend>1. Transaktion & försäljningsskäl</legend>

        <p className="risk-question">Kort om själva affären</p>
        <p className="risk-help">
          Här fångar vi grunden till transaktionen – vad som säljs, varför och på vilka villkor.
        </p>

        <label>
          Typ av affär{" "}
          <span className="risk-help-inline">(aktier, inkråm eller öppet)</span>
          <select
            value={formData.transaction.dealType}
            onChange={handleChange("transaction", "dealType")}
          >
            <option value="">Välj…</option>
            <option value="share">Försäljning av aktier</option>
            <option value="asset">Inkråmsöverlåtelse</option>
            <option value="either">Öppet – kan bli både aktier eller inkråm</option>
          </select>
        </label>

        <label>
          Andel av bolaget/verksamheten som säljs (%)
          <input
            type="number"
            min="0"
            max="100"
            value={formData.transaction.sharePercentage}
            onChange={handleChange("transaction", "sharePercentage")}
          />
        </label>

        <label>
          Försäljningsskäl (kort bakgrund)
          <textarea
            placeholder="Exempel: generationsskifte, fokus på annat bolag, ta in partner, minska risk m.m."
            value={formData.transaction.reasonForSale}
            onChange={handleChange("transaction", "reasonForSale")}
          />
        </label>

        <label>
          Ägarens roll efter transaktionen
          <textarea
            placeholder="Exempel: kvar heltid 12–24 månader, därefter rådgivande roll"
            value={formData.transaction.sellerRoleAfter}
            onChange={handleChange("transaction", "sellerRoleAfter")}
          />
        </label>

        <label>
          Vad ingår i affären?
          <textarea
            placeholder="Exempel: kundavtal, varumärken, domäner, maskiner, lager, personal m.m."
            value={formData.transaction.includedAssets}
            onChange={handleChange("transaction", "includedAssets")}
          />
        </label>

        <label>
          Vad ingår inte / undantag?
          <textarea
            placeholder="Exempel: fastighet, privata tillgångar, särskilda tillgångar som behålls"
            value={formData.transaction.excludedItems}
            onChange={handleChange("transaction", "excludedItems")}
          />
        </label>

        <label>
          Önskad tidplan / tillträdesdatum
          <input
            type="text"
            placeholder="Exempel: tillträde helst Q4 2025"
            value={formData.transaction.desiredClosingDate}
            onChange={handleChange("transaction", "desiredClosingDate")}
          />
        </label>

        <label>
          Önskad betalningsstruktur
          <textarea
            placeholder="Exempel: kontant betalning, earn-out, säljarrevers eller kombination"
            value={formData.transaction.dealStructure}
            onChange={handleChange("transaction", "dealStructure")}
          />
        </label>

        <label>
          Övrig information om transaktionen
          <textarea
            value={formData.transaction.otherNotes}
            onChange={handleChange("transaction", "otherNotes")}
          />
        </label>

        <label>
          Vad är bolagets starkaste konkurrensfördelar? (2–3 meningar)
          <textarea
            placeholder="Exempel: lång kundhistorik, unik kompetens, lokal närvaro, certifieringar"
            value={formData.transaction.competitiveAdvantages}
            onChange={handleChange("transaction", "competitiveAdvantages")}
          />
        </label>

        <label>
          Vilka tillväxtmöjligheter finns som nästa ägare kan realisera?
          <textarea
            placeholder="Exempel: nya geografier, korsförsäljning, fler tjänster, digitalisering, prishöjningar"
            value={formData.transaction.growthOpportunities}
            onChange={handleChange("transaction", "growthOpportunities")}
          />
        </label>
      </fieldset>

      {/* 2. Ägare & historik */}
      <fieldset className="risk-card">
        <legend>2. Ägare & historik</legend>

        <p className="risk-question">Ägarbild och bolagets resa</p>
        <p className="risk-help">
          Kort bild av vilka som äger bolaget, hur engagerade de är och hur bolaget har utvecklats.
        </p>

        <label>
          Antal ägare
          <input
            type="number"
            min="1"
            value={formData.ownership.numberOfShareholders}
            onChange={handleChange("ownership", "numberOfShareholders")}
          />
        </label>

        {/* Strukturerad ägarlista — rendreras som rader i IM:ets ägartabell.
            Varje rad har namn (kan vara person eller bolag), ägarandel (%) och
            roll (t.ex. VD, styrelseordförande, passiv). Tomma rader filtreras
            bort i toApiPayload. */}
        <div className="risk-card-sub" style={{ marginTop: 12 }}>
          <p className="risk-question" style={{ marginBottom: 4 }}>
            Ägare (namn, andel, roll)
          </p>
          <p className="risk-help" style={{ marginBottom: 12 }}>
            Lista enskilda ägare eller ägargrupper. Andel anges i procent. Namn
            kan anonymiseras till t.ex. "Huvudägare", "Familj A" om så önskas.
          </p>

          {(formData.ownership.shareholders || []).map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1.5fr auto",
                gap: 10,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <input
                type="text"
                placeholder={`Ägare ${idx + 1} (namn eller "Huvudägare")`}
                value={row.name || ""}
                onChange={(e) => {
                  const next = [...formData.ownership.shareholders];
                  next[idx] = { ...next[idx], name: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    ownership: { ...prev.ownership, shareholders: next },
                  }));
                }}
              />
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Andel %"
                value={row.sharePercent ?? ""}
                onChange={(e) => {
                  const next = [...formData.ownership.shareholders];
                  next[idx] = { ...next[idx], sharePercent: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    ownership: { ...prev.ownership, shareholders: next },
                  }));
                }}
              />
              <input
                type="text"
                placeholder="Roll (VD, styrelse, passiv…)"
                value={row.role || ""}
                onChange={(e) => {
                  const next = [...formData.ownership.shareholders];
                  next[idx] = { ...next[idx], role: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    ownership: { ...prev.ownership, shareholders: next },
                  }));
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const next = formData.ownership.shareholders.filter(
                    (_, i) => i !== idx,
                  );
                  setFormData((prev) => ({
                    ...prev,
                    ownership: { ...prev.ownership, shareholders: next },
                  }));
                }}
                aria-label="Ta bort ägare"
                style={{ padding: "6px 10px" }}
              >
                ✕
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => {
              const next = [
                ...(formData.ownership.shareholders || []),
                { name: "", sharePercent: "", role: "" },
              ];
              setFormData((prev) => ({
                ...prev,
                ownership: { ...prev.ownership, shareholders: next },
              }));
            }}
            style={{ marginTop: 4 }}
          >
            + Lägg till ägare
          </button>
        </div>

        <label>
          Kort beskrivning av ägarbilden
          <textarea
            placeholder="Exempel: en huvudägare, familjeägt, delägare aktiva i verksamheten"
            value={formData.ownership.ownerDescription}
            onChange={handleChange("ownership", "ownerDescription")}
          />
        </label>

        <label>
          Ägarnas involvering i vardagen
          <textarea
            placeholder="Exempel: operativ VD, arbetar deltid, enbart styrelsenivå"
            value={formData.ownership.ownerInvolvement}
            onChange={handleChange("ownership", "ownerInvolvement")}
          />
        </label>

        {/* Strukturerad Historik-tidslinje — samma rad-baserade
            pattern som shareholders och ebitda_adjustments. Composer
            föredrar denna över regex-parsen av group_structure så
            brokers slipper skriva "1981 …"-lines från fri hand. */}
        <div className="risk-card-sub" style={{ marginTop: 16 }}>
          <p className="risk-question" style={{ marginBottom: 4 }}>
            Historik och milstolpar
          </p>
          <p className="risk-help" style={{ marginBottom: 12 }}>
            Lägg till en rad per milstolpe (grundades, första lokal,
            förvärv, större kontrakt m.m.). Detta blir en tidslinje i
            IM:ets Historik-avsnitt. Årtal anges som fyra siffror
            (t.ex. 1996).
          </p>

          {(formData.ownership.historyMilestones || []).map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "100px 1fr auto",
                gap: 8,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <input
                type="number"
                min="1800"
                max="2100"
                placeholder="År"
                value={row.year}
                onChange={(e) => {
                  const next = [...formData.ownership.historyMilestones];
                  next[idx] = { ...next[idx], year: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    ownership: { ...prev.ownership, historyMilestones: next },
                  }));
                }}
              />
              <input
                type="text"
                placeholder={`Händelse ${idx + 1} (t.ex. "Bolaget grundades i Uppsala")`}
                value={row.event}
                onChange={(e) => {
                  const next = [...formData.ownership.historyMilestones];
                  next[idx] = { ...next[idx], event: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    ownership: { ...prev.ownership, historyMilestones: next },
                  }));
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const next = formData.ownership.historyMilestones.filter(
                    (_, i) => i !== idx,
                  );
                  setFormData((prev) => ({
                    ...prev,
                    ownership: { ...prev.ownership, historyMilestones: next },
                  }));
                }}
                aria-label={`Ta bort milstolpe ${idx + 1}`}
                style={{ padding: "4px 10px" }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const next = [
                ...(formData.ownership.historyMilestones || []),
                { year: "", event: "" },
              ];
              setFormData((prev) => ({
                ...prev,
                ownership: { ...prev.ownership, historyMilestones: next },
              }));
            }}
            style={{ marginTop: 4 }}
          >
            + Lägg till milstolpe
          </button>
        </div>

        <label>
          Koncernstruktur eller övrig historik (fri text)
          <span className="risk-help" style={{ display: "block", marginTop: 4, marginBottom: 6 }}>
            Beskriv koncern- eller bolagsstruktur, eller lägg till
            berättande bakgrund som inte får plats som milstolpar
            ovan. Fritext renderas som ett narrativt stycke.
          </span>
          <textarea
            rows={4}
            placeholder="Exempel: Bolaget är helägt dotterbolag till X Holding AB. Verksamheten bedrivs sedan 1990 från Uppsala."
            value={formData.ownership.groupStructure}
            onChange={handleChange("ownership", "groupStructure")}
          />
        </label>

        <label>
          Successions-/överlåtelseplanering
          <textarea
            placeholder="Exempel: generationsskifte, önskan att trappa ned, arvtagare i familjen m.m."
            value={formData.ownership.successionPlan}
            onChange={handleChange("ownership", "successionPlan")}
          />
        </label>
      </fieldset>

      {/* 3. Kunder & intäkter */}
      <fieldset className="risk-card">
        <legend>3. Kunder & intäkter</legend>

        <p className="risk-question">Kundbas och intäktsmix</p>
        <p className="risk-help">
          Här fångar vi storlek på kundbasen, vilka segment som är viktigast och hur intäktsmodellen ser ut.
          (Kundkoncentration bedöms i riskprofilen.)
        </p>

        <label>
          Ungefärligt antal aktiva kunder
          <input
            type="number"
            min="0"
            value={formData.customers.numberOfCustomers}
            onChange={handleChange("customers", "numberOfCustomers")}
          />
        </label>

        <label>
          Viktigaste kundsegment
          <textarea
            placeholder="Exempel: större industriföretag, offentliga kunder, SME inom X"
            value={formData.customers.mainSegments}
            onChange={handleChange("customers", "mainSegments")}
          />
        </label>

        <label>
          Kan ni nämna 3–5 kända kunder eller varumärken ni arbetat med?
          <span className="risk-help-inline">
            {" "}(visas anonymiserat eller med godkännande)
          </span>
          <textarea
            placeholder="Exempel: nordisk industrikoncern, ledande e-handelsaktör, två kommuner i Mellansverige"
            value={formData.customers.referenceCustomers}
            onChange={handleChange("customers", "referenceCustomers")}
          />
        </label>

        <label>
          Återkommande intäkter (abonnemang, avtal) – andel av omsättningen (%)
          <input
            type="number"
            min="0"
            max="100"
            value={formData.customers.recurringRevenuePercent}
            onChange={handleChange("customers", "recurringRevenuePercent")}
          />
        </label>

        <label>
          Projekt-/engångsintäkter – andel av omsättningen (%)
          <input
            type="number"
            min="0"
            max="100"
            value={formData.customers.projectRevenuePercent}
            onChange={handleChange("customers", "projectRevenuePercent")}
          />
        </label>

        <label>
          Genomsnittlig kontraktslängd (månader)
          <input
            type="number"
            min="0"
            value={formData.customers.averageContractLengthMonths}
            onChange={handleChange("customers", "averageContractLengthMonths")}
          />
        </label>

        <label>
          Finns särskilda nyckelkunds-/ramavtal?
          <select
            value={formData.customers.hasKeyAccountContracts}
            onChange={handleChange("customers", "hasKeyAccountContracts")}
          >
            <option value="">Välj…</option>
            <option value="yes">Ja</option>
            <option value="no">Nej</option>
          </select>
        </label>
      </fieldset>

      {/* 3b. Marknad & konkurrenter */}
      <fieldset className="risk-card">
        <legend>3b. Marknad & konkurrenter</legend>

        <p className="risk-question">Marknadens storlek, dynamik och konkurrensbild</p>
        <p className="risk-help">
          Kort orientering om vilka ni möter i upphandlingar/affärer, hur stor marknaden
          bedöms vara och var ni är aktiva geografiskt.
        </p>

        <label>
          Vilka är era 3–5 främsta konkurrenter?
          <textarea
            placeholder="Namnge eller beskriv (t.ex. 'två nordiska bolag i samma nisch + en internationell aktör')"
            value={formData.market.topCompetitors}
            onChange={handleChange("market", "topCompetitors")}
          />
        </label>

        <label>
          Hur stor bedömer ni marknaden vara och hur utvecklas den?
          <textarea
            placeholder="Exempel: nordisk marknad ~2 mdr SEK, växer 4–6 %/år drivet av X"
            value={formData.market.marketSizeAndTrend}
            onChange={handleChange("market", "marketSizeAndTrend")}
          />
        </label>

        <label>
          I vilka geografier är ni aktiva?
          <textarea
            placeholder="Exempel: Sverige (huvudmarknad), Norge och Finland; planer för Tyskland"
            value={formData.market.geographies}
            onChange={handleChange("market", "geographies")}
          />
        </label>
      </fieldset>

      {/* 4. Organisation & personal */}
      <fieldset className="risk-card">
        <legend>4. Organisation & personal</legend>

        <p className="risk-question">Team och organisation</p>
        <p className="risk-help">
          Hur ser organisationen ut idag, och var finns eventuella personberoenden eller rekryteringsutmaningar?
        </p>

        <label>
          Antal anställda (FTE)
          <input
            type="number"
            min="0"
            value={formData.organisation.headcountFTE}
            onChange={handleChange("organisation", "headcountFTE")}
          />
        </label>

        <label>
          Kort beskrivning av organisationen
          <textarea
            placeholder="Fördelning mellan sälj, leverans, produktion, administration m.m."
            value={formData.organisation.headcountDescription}
            onChange={handleChange("organisation", "headcountDescription")}
          />
        </label>

        <label>
          Kort beskrivning av ledningsgrupp/nyckelpersoner (roller, ej namn)
          <textarea
            value={formData.organisation.managementSummary}
            onChange={handleChange("organisation", "managementSummary")}
          />
        </label>

        <label>
          Personberoenden
          <textarea
            placeholder="Exempel: verksamheten är beroende av specifika personer/kompetenser"
            value={formData.organisation.keyPersonDependencies}
            onChange={handleChange("organisation", "keyPersonDependencies")}
          />
        </label>

        <label>
          Rekryteringsutmaningar
          <textarea
            placeholder="Exempel: svårt att hitta viss kompetens, lätt att rekrytera lokalt, etc."
            value={formData.organisation.recruitmentChallenges}
            onChange={handleChange("organisation", "recruitmentChallenges")}
          />
        </label>
      </fieldset>

      {/* 5. Drift, lokaler & leverantörer */}
      <fieldset className="risk-card">
        <legend>5. Drift, lokaler & leverantörer</legend>

        <p className="risk-question">Hur bolaget drivs i vardagen</p>
        <p className="risk-help">Lokaler, utrustning, lager och viktiga leverantörer/partners.</p>

        <label>
          Beskrivning av lokaler och geografisk närvaro
          <textarea
            placeholder="Exempel: huvudkontor, filialer, lager, produktion m.m."
            value={formData.operations.locationsDescription}
            onChange={handleChange("operations", "locationsDescription")}
          />
        </label>

        <label>
          Äger eller hyr bolaget sina lokaler?
          <select
            value={formData.operations.ownsPremises}
            onChange={handleChange("operations", "ownsPremises")}
          >
            <option value="">Välj…</option>
            <option value="owned">Främst ägda lokaler</option>
            <option value="leased">Främst hyrda lokaler</option>
            <option value="mixed">Både ägda och hyrda</option>
          </select>
        </label>

        <label>
          Maskinpark/utrustning
          <textarea
            placeholder="Kort beskrivning av större maskiner, fordon, produktionsutrustning"
            value={formData.operations.equipmentAndMachinery}
            onChange={handleChange("operations", "equipmentAndMachinery")}
          />
        </label>

        <label>
          Lager, logistik och distribution
          <textarea
            value={formData.operations.inventoryAndLogistics}
            onChange={handleChange("operations", "inventoryAndLogistics")}
          />
        </label>

        <label>
          Viktiga leverantörer/partners (anonymiserat)
          <textarea
            placeholder="Exempel: stor nordisk grossist, internationell molnleverantör m.m."
            value={formData.operations.keySuppliers}
            onChange={handleChange("operations", "keySuppliers")}
          />
        </label>

        <label>
          Leverantörsberoende
          <textarea
            placeholder="Exempel: beroende av en leverantör, lätt att byta, flera alternativ"
            value={formData.operations.supplierDependency}
            onChange={handleChange("operations", "supplierDependency")}
          />
        </label>

        <label>
          Certifieringar eller standarder
          <textarea
            placeholder="Exempel: ISO, branschstandarder etc."
            value={formData.operations.certifications}
            onChange={handleChange("operations", "certifications")}
          />
        </label>
      </fieldset>

      {/* 6. Juridik & avtal */}
      <fieldset className="risk-card">
        <legend>6. Juridik & avtal</legend>

        <p className="risk-question">Avtals- och regelverksläge</p>
        <p className="risk-help">
          Översikt av viktiga kund- och leverantörsavtal, eventuella tvister och särskilda regler/tillstånd.
        </p>

        <label>
          Viktiga kundavtal
          <textarea
            placeholder="Typ av avtal, längd, uppsägningstider (ej kundnamn)"
            value={formData.legal.keyCustomerContractsDesc}
            onChange={handleChange("legal", "keyCustomerContractsDesc")}
          />
        </label>

        <label>
          Viktiga leverantörsavtal
          <textarea
            value={formData.legal.keySupplierContractsDesc}
            onChange={handleChange("legal", "keySupplierContractsDesc")}
          />
        </label>

        <label>
          Hyres- och leasingavtal
          <textarea
            value={formData.legal.leaseAgreementsDesc}
            onChange={handleChange("legal", "leaseAgreementsDesc")}
          />
        </label>

        <label>
          Är verksamheten reglerad/tillståndspliktig?
          <select
            value={formData.legal.regulatedBusiness}
            onChange={handleChange("legal", "regulatedBusiness")}
          >
            <option value="">Välj…</option>
            <option value="yes">Ja</option>
            <option value="no">Nej</option>
            <option value="partial">Delvis / vissa delar</option>
          </select>
        </label>

        <label>
          Kort om särskilda tillstånd/krav
          <textarea
            value={formData.legal.regulatoryRequirements}
            onChange={handleChange("legal", "regulatoryRequirements")}
          />
        </label>

        <label>
          Finns pågående tvister eller krav?
          <select value={formData.legal.disputes} onChange={handleChange("legal", "disputes")}>
            <option value="">Välj…</option>
            <option value="yes">Ja</option>
            <option value="no">Nej</option>
          </select>
        </label>

        <label>
          Kort beskrivning av tvister/krav (om ja)
          <textarea
            value={formData.legal.disputesDescription}
            onChange={handleChange("legal", "disputesDescription")}
          />
        </label>

        <label>
          Övriga compliance-/juridikfrågor (GDPR, arbetsmiljö, miljö m.m.)
          <textarea
            value={formData.legal.complianceIssues}
            onChange={handleChange("legal", "complianceIssues")}
          />
        </label>

        <label>
          Andra juridiska risker eller avtalsfrågor
          <textarea
            value={formData.legal.otherLegalRisks}
            onChange={handleChange("legal", "otherLegalRisks")}
          />
        </label>
      </fieldset>

      {/* 7. Finansiella justeringar & prognos */}
      <fieldset className="risk-card">
        <legend>7. Finansiella justeringar & prognos</legend>

        <p className="risk-question">Justeringar inför värdering</p>
        <p className="risk-help">
          Här samlar vi ägarlön, ägarnära kostnader och engångsposter för att kunna räkna fram ett justerat resultat.
        </p>

        <label>
          Justering för ägarens lön/ersättning (per år, ungefärligt belopp)
          <input
            type="number"
            value={formData.financial.ownerSalaryAdjustment}
            onChange={handleChange("financial", "ownerSalaryAdjustment")}
          />
        </label>

        <label>
          Andra ägarnära justeringar (bil, pension, privata kostnader)
          <textarea
            value={formData.financial.otherOwnerRelatedAdjustments}
            onChange={handleChange("financial", "otherOwnerRelatedAdjustments")}
          />
        </label>

        <label>
          Engångsposter de senaste åren
          <textarea
            placeholder="Exempel: större projekt, engångskostnader, nedskrivningar m.m."
            value={formData.financial.nonRecurringItemsDesc}
            onChange={handleChange("financial", "nonRecurringItemsDesc")}
          />
        </label>

        {/* Strukturerad EBITDA-bridge — professionell CIM-nivå.
            Varje rad blir en post i den "normaliserade EBITDA"-tabellen
            i det renderade IM:et. Rekommenderat 3–8 poster:
            ägarens tjänste­bil, privat sjukvårds­försäkring, engångs­
            kostnader, familje­löner, hyres­marknads­justering m.m.
            Fältet omfattar även DEDUCT-poster (t.ex. under­marknads­
            hyra som skulle behöva ökas under ny ägare). */}
        <div className="risk-card-sub" style={{ marginTop: 16 }}>
          <p className="risk-question" style={{ marginBottom: 4 }}>
            EBITDA-justeringar (bridge)
          </p>
          <p className="risk-help" style={{ marginBottom: 12 }}>
            Lägg till en rad per justering. Detta blir "Normaliserad
            EBITDA-bridge"-tabellen i IM:et. Belopp anges i tkr (kSEK).
            Använd "Återlägg" (add back) för poster som ökar EBITDA
            under ny ägare (ägarens tjänstebil, engångs­kostnader) och
            "Avdrag" (deduct) för poster som minskar EBITDA (t.ex.
            under­marknads­hyra som måste normaliseras uppåt).
          </p>

          {(formData.financial.ebitdaAdjustments || []).map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr 1.5fr 2fr auto",
                gap: 8,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <input
                type="text"
                placeholder={`Post ${idx + 1} (t.ex. "Ägarens tjänstebil")`}
                value={row.label || ""}
                onChange={(e) => {
                  const next = [...formData.financial.ebitdaAdjustments];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, ebitdaAdjustments: next },
                  }));
                }}
              />
              <input
                type="number"
                placeholder="kSEK"
                value={row.amountKsek}
                onChange={(e) => {
                  const next = [...formData.financial.ebitdaAdjustments];
                  next[idx] = { ...next[idx], amountKsek: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, ebitdaAdjustments: next },
                  }));
                }}
              />
              <select
                value={row.direction || "add_back"}
                onChange={(e) => {
                  const next = [...formData.financial.ebitdaAdjustments];
                  next[idx] = { ...next[idx], direction: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, ebitdaAdjustments: next },
                  }));
                }}
              >
                <option value="add_back">Återlägg (+)</option>
                <option value="deduct">Avdrag (−)</option>
              </select>
              <select
                value={row.category || ""}
                onChange={(e) => {
                  const next = [...formData.financial.ebitdaAdjustments];
                  next[idx] = { ...next[idx], category: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, ebitdaAdjustments: next },
                  }));
                }}
              >
                <option value="">Kategori…</option>
                <option value="owner_salary">Ägarlön</option>
                <option value="owner_perk">Ägarförmån</option>
                <option value="family_payroll">Familjeanställd</option>
                <option value="one_off_cost">Engångskostnad</option>
                <option value="one_off_gain">Engångsintäkt</option>
                <option value="related_party_rent">Närståendehyra</option>
                <option value="discontinued">Avvecklad verksamhet</option>
                <option value="other">Övrigt</option>
              </select>
              <input
                type="text"
                placeholder="Anteckning / motivering"
                value={row.notes || ""}
                onChange={(e) => {
                  const next = [...formData.financial.ebitdaAdjustments];
                  next[idx] = { ...next[idx], notes: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, ebitdaAdjustments: next },
                  }));
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const next = formData.financial.ebitdaAdjustments.filter(
                    (_, i) => i !== idx,
                  );
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, ebitdaAdjustments: next },
                  }));
                }}
                aria-label={`Ta bort justering ${idx + 1}`}
                style={{ padding: "4px 10px" }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const next = [
                ...(formData.financial.ebitdaAdjustments || []),
                { label: "", amountKsek: "", direction: "add_back", category: "", notes: "" },
              ];
              setFormData((prev) => ({
                ...prev,
                financial: { ...prev.financial, ebitdaAdjustments: next },
              }));
            }}
            style={{ marginTop: 4 }}
          >
            + Lägg till justering
          </button>
        </div>

        <label>
          Finns budget/prognos?
          <select
            value={formData.financial.forecastAvailable}
            onChange={handleChange("financial", "forecastAvailable")}
          >
            <option value="">Välj…</option>
            <option value="yes">Ja</option>
            <option value="no">Nej</option>
          </select>
        </label>

        <label>
          Kort sammanfattning av budget/prognos och antaganden
          <textarea
            value={formData.financial.forecastSummary}
            onChange={handleChange("financial", "forecastSummary")}
          />
        </label>

        {/* Strukturerad 3-årsprognos — professionell CIM-nivå.
            Radbaserat, samma pattern som ebitda_adjustments och
            shareholders. En rad per prognosår: omsättning (MSEK),
            EBITDA (MSEK), och valfritt tillväxt % + marginal %.
            Om säljaren lämnar tillväxt/marginal tomma räknas de ut
            deterministiskt av composern från revenue+EBITDA-endpoints. */}
        <div className="risk-card-sub" style={{ marginTop: 16 }}>
          <p className="risk-question" style={{ marginBottom: 4 }}>
            Prognos per räkenskapsår (3 år)
          </p>
          <p className="risk-help" style={{ marginBottom: 12 }}>
            Lägg till en rad per prognosår (typiskt de kommande 3
            räkenskapsåren). Detta blir "Säljarens prognos"-tabellen
            i IM:et, presenterad sida vid sida med senaste utfallet.
            Fyll i minst år + omsättning + EBITDA; tillväxt % och
            marginal % räknas ut automatiskt om lämnade tomma.
          </p>

          {(formData.financial.forecastProjections || []).map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "0.7fr 1fr 1fr 1fr 1fr 1.5fr auto",
                gap: 8,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <input
                type="number"
                placeholder="År"
                value={row.year}
                onChange={(e) => {
                  const next = [...formData.financial.forecastProjections];
                  next[idx] = { ...next[idx], year: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, forecastProjections: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="Omsättning (MSEK)"
                value={row.revenueMsek}
                onChange={(e) => {
                  const next = [...formData.financial.forecastProjections];
                  next[idx] = { ...next[idx], revenueMsek: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, forecastProjections: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="EBITDA (MSEK)"
                value={row.ebitdaMsek}
                onChange={(e) => {
                  const next = [...formData.financial.forecastProjections];
                  next[idx] = { ...next[idx], ebitdaMsek: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, forecastProjections: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="Tillväxt %"
                value={row.growthPct}
                onChange={(e) => {
                  const next = [...formData.financial.forecastProjections];
                  next[idx] = { ...next[idx], growthPct: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, forecastProjections: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="Marginal %"
                value={row.marginPct}
                onChange={(e) => {
                  const next = [...formData.financial.forecastProjections];
                  next[idx] = { ...next[idx], marginPct: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, forecastProjections: next },
                  }));
                }}
              />
              <input
                type="text"
                placeholder="Notering (valfritt)"
                value={row.notes || ""}
                onChange={(e) => {
                  const next = [...formData.financial.forecastProjections];
                  next[idx] = { ...next[idx], notes: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, forecastProjections: next },
                  }));
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const next = formData.financial.forecastProjections.filter(
                    (_, i) => i !== idx,
                  );
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, forecastProjections: next },
                  }));
                }}
                aria-label={`Ta bort prognosår ${idx + 1}`}
                style={{ padding: "4px 10px" }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const next = [
                ...(formData.financial.forecastProjections || []),
                {
                  year: "",
                  revenueMsek: "",
                  ebitdaMsek: "",
                  growthPct: "",
                  marginPct: "",
                  notes: "",
                },
              ];
              setFormData((prev) => ({
                ...prev,
                financial: { ...prev.financial, forecastProjections: next },
              }));
            }}
            style={{ marginTop: 4 }}
          >
            + Lägg till prognosår
          </button>
        </div>

        {/* Kvantifierade tillväxtinitiativ — SME-professionell CIM-nivå.
            Ersätter den fria growth_opportunities-texten med
            strukturerade planer: marknad, tidplan, investering,
            break-even, år-3 målsättning. Läses av value_creation-
            composern och renderas som tabell + notering per initiativ. */}
        <div className="risk-card-sub" style={{ marginTop: 16 }}>
          <p className="risk-question" style={{ marginBottom: 4 }}>
            Kvantifierade tillväxtinitiativ
          </p>
          <p className="risk-help" style={{ marginBottom: 12 }}>
            Lägg till varje planerat tillväxtinitiativ som en rad. Etikett
            + marknad räcker för att aktivera; övriga fält är valfria men
            gör raden mer säljbar. Detta blir "Kvantifierade tillväxt­
            initiativ"-tabellen i Värdeskapande-sektionen.
          </p>

          {(formData.financial.growthInitiatives || []).map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 2fr auto",
                gap: 6,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <input
                type="text"
                placeholder="Etikett (t.ex. 'Nordisk expansion')"
                value={row.label}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="text"
                placeholder="Marknad"
                value={row.market}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = { ...next[idx], market: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="text"
                placeholder="Start (Q3 2026)"
                value={row.timing}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = { ...next[idx], timing: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="Invest. MSEK"
                value={row.investmentMsek}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = { ...next[idx], investmentMsek: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="number"
                placeholder="Break-even (mån)"
                value={row.breakevenMonths}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = { ...next[idx], breakevenMonths: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="Commit. MSEK"
                value={row.initialCommitmentMsek}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = {
                    ...next[idx],
                    initialCommitmentMsek: e.target.value,
                  };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="År-3 omsättn."
                value={row.annualRevenueTargetMsek}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = {
                    ...next[idx],
                    annualRevenueTargetMsek: e.target.value,
                  };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="number"
                step="0.1"
                placeholder="År-3 EBITDA"
                value={row.annualEbitdaTargetMsek}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = {
                    ...next[idx],
                    annualEbitdaTargetMsek: e.target.value,
                  };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <input
                type="text"
                placeholder="Notering (prospects, motiv)"
                value={row.notes || ""}
                onChange={(e) => {
                  const next = [...formData.financial.growthInitiatives];
                  next[idx] = { ...next[idx], notes: e.target.value };
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const next = formData.financial.growthInitiatives.filter(
                    (_, i) => i !== idx,
                  );
                  setFormData((prev) => ({
                    ...prev,
                    financial: { ...prev.financial, growthInitiatives: next },
                  }));
                }}
                aria-label={`Ta bort initiativ ${idx + 1}`}
                style={{ padding: "4px 10px" }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const next = [
                ...(formData.financial.growthInitiatives || []),
                {
                  label: "",
                  market: "",
                  timing: "",
                  investmentMsek: "",
                  breakevenMonths: "",
                  initialCommitmentMsek: "",
                  annualRevenueTargetMsek: "",
                  annualEbitdaTargetMsek: "",
                  notes: "",
                },
              ];
              setFormData((prev) => ({
                ...prev,
                financial: { ...prev.financial, growthInitiatives: next },
              }));
            }}
            style={{ marginTop: 4 }}
          >
            + Lägg till tillväxtinitiativ
          </button>
        </div>
      </fieldset>

      {/* Image intake wizard. Asks the seller — one row per IM image
          slot — whether they have a photo they want to use. Only
          rows answered "Ja" are revealed as upload cards below.
          Persisted via the questionnaire bundle (image_role_intents
          JSONField on OwnerQuestionnaire) so the choices survive
          page reloads. */}
      <ImageIntakeWizard
        intents={formData.imageRoleIntents || {}}
        onChange={(role, value) =>
          setFormData((prev) => ({
            ...prev,
            imageRoleIntents: { ...(prev.imageRoleIntents || {}), [role]: value },
          }))
        }
      />

      {/* Bilder till IM — seller/broker uploads that override the
          web-scraped assets in the backend IM renderer. Self-contained:
          fetches, uploads and deletes are handled inside the component
          via its own endpoints, independent of the questionnaire's
          bundle save. The visibleRoleValues set narrows the grid to
          only the slots the seller said "Ja" to in the wizard above. */}
      <OwnerImageUploader
        orgnr={orgnr}
        visibleRoleValues={
          new Set(
            Object.entries(formData.imageRoleIntents || {})
              .filter(([, v]) => v === "yes")
              .map(([k]) => k)
          )
        }
      />

      <button type="submit" disabled={saving || !orgnr} className="questionnaire-submit">
        {saving ? "Sparar…" : "Spara ägaruppgifter"}
      </button>

      {!orgnr && (
        <p className="questionnaire-meta">
          (Ägaruppgifterna sparas först när organisationsnummer är kopplat till bolaget.)
        </p>
      )}
    </form>
  );
}

export default OwnerQuestionnaireForm;

// --------------------------------------------------------
// Image intake wizard
// --------------------------------------------------------
//
// Per-role Ja/Nej questions that gate which upload slots show up in
// OwnerImageUploader. The seller sees one row per IM image slot
// (hero / logo / team / office / product / certification / section)
// with two buttons. Answering "Ja" reveals the matching upload card
// below; "Nej" keeps it hidden. Unanswered rows show both buttons
// unselected and keep their slot hidden until the seller decides.
function ImageIntakeWizard({ intents, onChange }) {
  return (
    <fieldset className="risk-card">
      <legend>Vilka bilder vill du använda i IM:en?</legend>
      <p className="risk-help">
        Markera de bildslotar du har egna foton till. Rutor för
        uppladdning visas nedan för de rader du svarat <strong>Ja</strong> på.
        Du kan ändra dig när som helst — svaret sparas med formuläret.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
        {OWNER_IMAGE_ROLES.map(({ value, label }) => {
          const answer = intents?.[value];
          return (
            <div
              key={value}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "8px 12px",
                border: "1px solid #e2e2df",
                borderRadius: 6,
                background: answer === "yes" ? "#f3f8f3" : "#fafaf7",
              }}
            >
              <div style={{ fontSize: 13, color: "#1c1917", fontWeight: 500 }}>
                {label}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => onChange(value, "yes")}
                  style={{
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    border: "1px solid",
                    borderColor: answer === "yes" ? "#1c6e3a" : "#d6d3d1",
                    background: answer === "yes" ? "#1c6e3a" : "#ffffff",
                    color: answer === "yes" ? "#ffffff" : "#1c1917",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Ja
                </button>
                <button
                  type="button"
                  onClick={() => onChange(value, "no")}
                  style={{
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    border: "1px solid",
                    borderColor: answer === "no" ? "#78716c" : "#d6d3d1",
                    background: answer === "no" ? "#78716c" : "#ffffff",
                    color: answer === "no" ? "#ffffff" : "#1c1917",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Nej
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

// --------------------------------------------------------
// Helpers
// --------------------------------------------------------

function emptyForm() {
  return {
    transaction: {
      dealType: "",
      sharePercentage: "",
      reasonForSale: "",
      sellerRoleAfter: "",
      includedAssets: "",
      excludedItems: "",
      desiredClosingDate: "",
      dealStructure: "",
      otherNotes: "",
      competitiveAdvantages: "",
      growthOpportunities: "",
    },
    ownership: {
      numberOfShareholders: "",
      ownerDescription: "",
      ownerInvolvement: "",
      groupStructure: "",
      successionPlan: "",
      shareholders: [],
      historyMilestones: [],
    },
    customers: {
      numberOfCustomers: "",
      mainSegments: "",
      referenceCustomers: "",
      recurringRevenuePercent: "",
      projectRevenuePercent: "",
      averageContractLengthMonths: "",
      hasKeyAccountContracts: "",
    },
    market: {
      topCompetitors: "",
      marketSizeAndTrend: "",
      geographies: "",
    },
    organisation: {
      headcountFTE: "",
      headcountDescription: "",
      managementSummary: "",
      keyPersonDependencies: "",
      recruitmentChallenges: "",
    },
    operations: {
      locationsDescription: "",
      ownsPremises: "",
      equipmentAndMachinery: "",
      inventoryAndLogistics: "",
      keySuppliers: "",
      supplierDependency: "",
      certifications: "",
    },
    legal: {
      keyCustomerContractsDesc: "",
      keySupplierContractsDesc: "",
      leaseAgreementsDesc: "",
      regulatedBusiness: "",
      regulatoryRequirements: "",
      disputes: "",
      disputesDescription: "",
      complianceIssues: "",
      otherLegalRisks: "",
    },
    financial: {
      ownerSalaryAdjustment: "",
      otherOwnerRelatedAdjustments: "",
      nonRecurringItemsDesc: "",
      ebitdaAdjustments: [],
      forecastAvailable: "",
      forecastSummary: "",
      forecastProjections: [],
      growthInitiatives: [],
    },
    // Image intake wizard. Per-role intent map ("yes" / "no" /
    // undefined). Only roles whose intent is "yes" show up as upload
    // slots in OwnerImageUploader. Backed by
    // ``OwnerQuestionnaire.image_role_intents`` on save.
    imageRoleIntents: {},
  };
}
